import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { CalendarIcon, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { Experiment, UpdateExperimentInput } from "@/types/experiment";
import { Plot } from "@/types/plot";
import { Timestamp } from "firebase/firestore";
import UpdatePlotMetadataSection from "./UpdatePlotMetadataSection";
import LocationSelector from "@/components/LocationSelector";
import { useLocations } from "@/hooks/useFirestore";
import { useAuth } from "@/contexts/AuthContext";

const experimentSchema = z.object({
  name: z.string().min(1, "Experiment name is required"),
  description: z.string().optional(),
  location: z.string().optional(),
  startDate: z.date().optional().nullable(),
  endDate: z.date().optional().nullable(),
}).refine((data) => {
  // Validate that end date is after start date if both are set
  if (data.startDate && data.endDate) {
    return data.endDate >= data.startDate;
  }
  return true;
}, {
  message: "End date must be after start date",
  path: ["endDate"],
});

// Helper function to determine how plots were created
function getPlotOrigin(experiment: Experiment): "upload" | "generate" | "unknown" {
  // Check for uploaded file indicators
  if (experiment.uploadedPlotMap?.url) {
    return "upload";
  }
  if (experiment.metadata?.csvFileName) {
    return "upload";
  }
  // Check for generated grid indicators
  if (experiment.plotMap?.numRows && experiment.plotMap?.numCols && !experiment.metadata?.csvFileName) {
    return "generate";
  }
  return "unknown";
}


interface EditExperimentFormProps {
  experiment: Experiment;
  plots: Plot[];
  onSubmit: (data: UpdateExperimentInput) => void;
  onCancel: () => void;
  onDelete?: () => void;
  onMetadataUpdate?: (parsedData: Record<string, string>[]) => void;
  showDelete?: boolean;
  /** Reports whether the form has unsaved edits, so the parent dialog can guard against accidental close. */
  onDirtyChange?: (dirty: boolean) => void;
}

const EditExperimentForm: React.FC<EditExperimentFormProps> = ({
  experiment,
  plots,
  onSubmit,
  onCancel,
  onDelete,
  onMetadataUpdate,
  showDelete = false,
  onDirtyChange,
}) => {
  const { userRole } = useAuth();
  const { locations, loading: locationsLoading } = useLocations();

  // Only editors and admins can add new locations
  const canEdit = userRole && ["editor", "admin"].includes(userRole);

  // Compute initial orphan location (location in experiment but not in org's locations list)
  const initialOrphanLocation = useMemo(() => {
    if (locationsLoading || !experiment.location) return undefined;
    return !locations.some(l => l.name === experiment.location) ? experiment.location : undefined;
  }, [experiment.location, locations, locationsLoading]);

  const form = useForm<z.infer<typeof experimentSchema>>({
    resolver: zodResolver(experimentSchema),
    defaultValues: {
      name: experiment.name,
      description: experiment.description || "",
      location: experiment.location || "",
      startDate: experiment.startDate?.toDate() || null,
      endDate: experiment.endDate?.toDate() || null,
    },
  });

  // A metadata CSV/Excel file selected in the update section but not yet applied
  // is unsaved work too, so fold it into the dirty signal.
  const [hasPendingMetadata, setHasPendingMetadata] = useState(false);

  // Surface unsaved-edit state so the wrapping dialog can confirm before discarding.
  const isDirty = form.formState.isDirty || hasPendingMetadata;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const handleFormSubmit = (data: z.infer<typeof experimentSchema>) => {
    const updateData: UpdateExperimentInput = {
      name: data.name,
      description: data.description || "",
      location: data.location || "",
      startDate: data.startDate ? Timestamp.fromDate(data.startDate) : undefined,
      endDate: data.endDate ? Timestamp.fromDate(data.endDate) : undefined,
    };

    onSubmit(updateData);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Experiment Name*</FormLabel>
              <FormControl>
                <Input placeholder="Enter experiment name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Describe the experiment details"
                  className="min-h-[80px] resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="location"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Location</FormLabel>
              <FormControl>
                <LocationSelector
                  locations={locations}
                  locationsLoading={locationsLoading}
                  selectedLocation={field.value || ""}
                  onSelectLocation={field.onChange}
                  canAddLocation={canEdit}
                  placeholder="Select a location"
                  initialOrphanLocation={initialOrphanLocation}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Start Date</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}>
                        {field.value ? format(field.value, "PPP") : <span>Select a date</span>}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value || undefined}
                      onSelect={field.onChange}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>End Date</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}>
                        {field.value ? format(field.value, "PPP") : <span>Select a date</span>}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value || undefined}
                      onSelect={field.onChange}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Update Plot Metadata Section - only for experiments created from CSV/Excel upload */}
        {getPlotOrigin(experiment) === "upload" && onMetadataUpdate && (
          <div className="border-t pt-4 mt-4">
            <UpdatePlotMetadataSection
              experiment={experiment}
              existingPlots={plots}
              onUpdate={onMetadataUpdate}
              onPendingChange={setHasPendingMetadata}
            />
          </div>
        )}

        <div className="flex justify-between pt-4">
          <div>
            {showDelete && onDelete && (
              <Button
                type="button"
                variant="destructive"
                onClick={onDelete}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Experiment
              </Button>
            )}
          </div>
          <div className="flex space-x-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" className="bg-agri-green hover:bg-agri-green-dark">
              Save Changes
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};

export default EditExperimentForm;
